import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import Navbar from '../../components/Navbar/Navbar';
import {
  pastDaysOfCurrentWeek,
  currentWeekEndBoundary,
  formatDayLabel,
  formatMxTime,
} from '../../lib/mxWeek';
import '../Requirements/RequirementForm.css';
import './TimeLogPermissions.css';

/* ── Icons ──────────────────────────────────────── */
function IconArrowLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
function IconChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function IconWarning() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconSpinner() {
  return (
    <svg className="rf-spinner-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}
function IconSuccess() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/* ── Map backend error codes to Spanish messages ── */
function mapPermError(msg) {
  if (!msg) return 'No se pudo otorgar el permiso.';
  if (msg.includes('PERM_NOT_GERENTE')) return 'No tienes autorización para otorgar permisos.';
  if (msg.includes('PERM_BAD_TARGET_ROLE')) return 'El usuario seleccionado no tiene rol Líder ni Desarrollador.';
  if (msg.includes('PERM_NOT_PAST')) return 'Solo puedes otorgar permisos para días que ya pasaron.';
  if (msg.includes('PERM_PAST_WEEK')) return 'Ese día pertenece a una semana anterior. Como Gerente, edítalo tú mismo.';
  if (msg.includes('PERM_BAD_DURATION') || msg.includes('duration_hours')) return 'La duración debe ser de entre 1 y 6 horas.';
  if (msg.includes('PERM_WEEK_OVERFLOW')) return 'El permiso terminaría en la siguiente semana. Reduce la duración o edita el registro tú mismo.';
  return msg;
}

const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6];

/* ── Component ──────────────────────────────────── */
function TimeLogPermissions() {
  const navigate = useNavigate();

  /* users available for the picker (Líder / Desarrollador) */
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  /* form */
  const [selectedUser, setSelectedUser] = useState(null);
  const [logDate, setLogDate] = useState('');
  const [durationHours, setDurationHours] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /* user search modal */
  const [showUserModal, setShowUserModal] = useState(false);
  const [userQuery, setUserQuery] = useState('');

  /* active grants */
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  /* tick — refresh "now" so countdowns, expiry preview and past-day options
     stay correct across the day (and the midnight rollover) */
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  /* ── Fetch users ── */
  useEffect(() => {
    (async () => {
      setUsersLoading(true);
      const { data } = await supabase
        .from('users')
        .select('id, full_name, roles(name)')
        .eq('active', true)
        .order('full_name');
      setUsers(
        (data || []).filter((u) => u.roles?.name === 'Líder' || u.roles?.name === 'Desarrollador')
      );
      setUsersLoading(false);
    })();
  }, []);

  /* ── Fetch active grants (silent = background refresh, no spinner) ── */
  const fetchGrants = useCallback(async (silent = false) => {
    if (!silent) setGrantsLoading(true);
    const { data } = await supabase
      .from('time_log_permissions')
      .select('id, user_id, log_date, duration_hours, granted_at, expires_at, revoked_at, user:users!user_id(full_name), granter:users!granted_by_user_id(full_name)')
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('expires_at', { ascending: true });
    setGrants(data || []);
    if (!silent) setGrantsLoading(false);
  }, []);

  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  // Background refresh on each tick so expired grants drop off on their own.
  useEffect(() => {
    fetchGrants(true);
  }, [nowTick, fetchGrants]);

  /* ── Past-day options (recomputed across midnight via nowTick) ── */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pastDays = useMemo(() => pastDaysOfCurrentWeek(), [nowTick]);
  const noPastDays = pastDays.length === 0;

  /* If the selected day falls out of range (e.g. midnight rollover), clear it */
  useEffect(() => {
    if (logDate && !pastDays.includes(logDate)) setLogDate('');
  }, [pastDays, logDate]);

  /* ── Expiry preview / week overflow ── */
  const expiryPreview = useMemo(
    () => new Date(nowTick + durationHours * 3600 * 1000),
    [nowTick, durationHours]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekBoundary = useMemo(() => currentWeekEndBoundary(), [nowTick]);
  const overflow = expiryPreview.getTime() > weekBoundary.getTime();

  /* ── User search filtering ── */
  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.full_name.toLowerCase().includes(q));
  }, [users, userQuery]);

  function openUserModal() {
    setUserQuery('');
    setShowUserModal(true);
  }
  function pickUser(u) {
    setSelectedUser(u);
    setShowUserModal(false);
    setErrors((prev) => ({ ...prev, user: '' }));
    setSubmitError('');
  }

  /* ── Validate ── */
  function validate() {
    const errs = {};
    if (!selectedUser) errs.user = 'Selecciona un usuario';
    if (!logDate) errs.logDate = 'Selecciona un día';
    if (!durationHours || durationHours < 1 || durationHours > 6) errs.duration = 'Duración inválida';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    if (overflow) {
      setSubmitError('El permiso terminaría en la siguiente semana. Reduce la duración o edita el registro tú mismo.');
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from('time_log_permissions')
      .insert([{
        user_id: selectedUser.id,
        log_date: logDate,
        duration_hours: durationHours,
      }]);
    setSaving(false);

    if (error) {
      setSubmitError(mapPermError(error.message));
      return;
    }

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setLogDate('');
    setDurationHours(1);
    setErrors({});
    fetchGrants();
  }

  /* ── Revoke ── */
  async function confirmRevoke() {
    if (!pendingRevoke) return;
    setRevoking(true);
    const { error } = await supabase.rpc('revoke_timelog_permission', { p_id: pendingRevoke.id });
    setRevoking(false);
    if (error) {
      setPendingRevoke(null);
      setSubmitError(mapPermError(error.message));
      return;
    }
    setPendingRevoke(null);
    fetchGrants();
  }

  /* ── Time-remaining label for an active grant ── */
  function remainingLabel(expiresAt) {
    const ms = new Date(expiresAt).getTime() - nowTick;
    if (ms <= 0) return 'Expirando…';
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `${h}h ${m}m restantes`;
    return `${m}m restantes`;
  }

  return (
    <div className="cat-page">
      <Navbar />
      <main className="cat-main">
        <div className="rf-container">
          {/* Header */}
          <div className="rf-header">
            <button className="rf-back-btn" onClick={() => navigate('/admin')} type="button">
              <IconArrowLeft />
              <span>Regresar</span>
            </button>
            <h1 className="rf-title">Permisos de Bitácora</h1>
          </div>

          <p className="tlp-intro">
            Otorga a un usuario con rol <strong>Líder</strong> o <strong>Desarrollador</strong> un
            permiso temporal para crear, editar o eliminar actividades en un día que ya pasó dentro
            de la semana actual. El permiso inicia al otorgarse y dura entre 1 y 6 horas.
          </p>

          {/* Success toast */}
          {showSuccess && (
            <div className="rf-toast">
              <IconSuccess />
              <span>Permiso otorgado correctamente.</span>
            </div>
          )}

          {/* ── Form ── */}
          <form className="rf-form" onSubmit={handleSubmit} noValidate>
            {/* Usuario */}
            <div className={`rf-field rf-field--full${errors.user ? ' rf-field--error' : ''}`}>
              <label className="rf-label">
                Usuario <span className="rf-required">*</span>
              </label>
              <button
                type="button"
                className="tlp-user-trigger"
                onClick={openUserModal}
                disabled={usersLoading}
              >
                <span className="tlp-user-trigger-icon"><IconUser /></span>
                {selectedUser ? (
                  <span className="tlp-user-trigger-name">
                    {selectedUser.full_name}
                    <span className="tlp-user-role-badge">{selectedUser.roles?.name}</span>
                  </span>
                ) : (
                  <span className="tlp-user-trigger-placeholder">
                    {usersLoading ? 'Cargando usuarios…' : 'Seleccionar usuario'}
                  </span>
                )}
                <span className="tlp-user-trigger-search"><IconSearch /></span>
              </button>
              {errors.user && <span className="rf-error-msg">{errors.user}</span>}
            </div>

            {/* Día a desbloquear */}
            <div className={`rf-field${errors.logDate ? ' rf-field--error' : ''}`}>
              <label className="rf-label">
                Día a desbloquear <span className="rf-required">*</span>
              </label>
              {noPastDays ? (
                <div className="tlp-nopast">
                  <IconWarning />
                  <span>
                    Hoy es el primer día de la semana, no hay días pasados disponibles. Si necesitas
                    corregir un día de una semana anterior, edítalo tú mismo como Gerente.
                  </span>
                </div>
              ) : (
                <div className="rf-select-wrap">
                  <select
                    className="rf-select"
                    value={logDate}
                    onChange={(e) => {
                      setLogDate(e.target.value);
                      setErrors((p) => ({ ...p, logDate: '' }));
                      setSubmitError('');
                    }}
                  >
                    <option value="">— Seleccionar día —</option>
                    {pastDays.map((d) => (
                      <option key={d} value={d}>
                        {formatDayLabel(d).replace(/^\w/, (c) => c.toUpperCase())}
                      </option>
                    ))}
                  </select>
                  <IconChevronDown />
                </div>
              )}
              {errors.logDate && <span className="rf-error-msg">{errors.logDate}</span>}
            </div>

            {/* Duración */}
            <div className={`rf-field${errors.duration ? ' rf-field--error' : ''}`}>
              <label className="rf-label">
                Duración <span className="rf-required">*</span>
              </label>
              <div className="rf-select-wrap">
                <select
                  className="rf-select"
                  value={durationHours}
                  onChange={(e) => {
                    setDurationHours(Number(e.target.value));
                    setSubmitError('');
                  }}
                  disabled={noPastDays}
                >
                  {DURATION_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h} {h === 1 ? 'hora' : 'horas'}</option>
                  ))}
                </select>
                <IconChevronDown />
              </div>
            </div>

            {/* Expiry preview */}
            {!noPastDays && (
              <div className="rf-field--full">
                <div className={`tlp-expiry${overflow ? ' tlp-expiry--overflow' : ''}`}>
                  <IconClock />
                  {overflow ? (
                    <span>
                      Con {durationHours} {durationHours === 1 ? 'hora' : 'horas'} el permiso
                      terminaría en la siguiente semana. Reduce la duración.
                    </span>
                  ) : (
                    <span>
                      El permiso vencería aproximadamente a las <strong>{formatMxTime(expiryPreview)} hrs</strong> (hora de México).
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Submit error */}
            {submitError && (
              <div className="rf-submit-error rf-field--full">
                <IconWarning />
                <span>{submitError}</span>
              </div>
            )}

            {/* Actions */}
            <div className="rf-actions rf-field--full">
              <button
                type="button"
                className="rf-btn rf-btn--cancel"
                onClick={() => navigate('/admin')}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rf-btn rf-btn--submit"
                disabled={saving || noPastDays || overflow}
              >
                {saving ? (
                  <>
                    <IconSpinner />
                    <span>Otorgando…</span>
                  </>
                ) : (
                  <span>Otorgar permiso</span>
                )}
              </button>
            </div>
          </form>

          {/* ── Active grants ── */}
          <section className="tlp-grants">
            <h2 className="tlp-grants-title">Permisos activos</h2>
            {grantsLoading ? (
              <div className="tlp-grants-empty">Cargando…</div>
            ) : grants.length === 0 ? (
              <div className="tlp-grants-empty">No hay permisos activos en este momento.</div>
            ) : (
              <ul className="tlp-grant-list">
                {grants.map((g) => (
                  <li key={g.id} className="tlp-grant-card">
                    <div className="tlp-grant-info">
                      <span className="tlp-grant-user">{g.user?.full_name || '—'}</span>
                      <span className="tlp-grant-day">
                        {formatDayLabel(g.log_date).replace(/^\w/, (c) => c.toUpperCase())}
                      </span>
                      <span className="tlp-grant-meta">
                        <IconClock />
                        {remainingLabel(g.expires_at)} · vence {formatMxTime(new Date(g.expires_at))} hrs
                      </span>
                      <span className="tlp-grant-granter">Otorgado por {g.granter?.full_name || '—'}</span>
                    </div>
                    <button
                      type="button"
                      className="tlp-revoke-btn"
                      onClick={() => setPendingRevoke(g)}
                    >
                      Revocar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      {/* ── User search modal ── */}
      {showUserModal && (
        <div className="rf-modal-backdrop" onClick={() => setShowUserModal(false)}>
          <div className="tlp-user-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="tlp-user-modal-title">Seleccionar usuario</h3>
            <div className="tlp-search-wrap">
              <IconSearch />
              <input
                type="text"
                className="tlp-search-input"
                placeholder="Buscar por nombre…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="tlp-user-results">
              {filteredUsers.length === 0 ? (
                <li className="tlp-user-result-empty">Sin coincidencias</li>
              ) : (
                filteredUsers.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      className={`tlp-user-result${selectedUser?.id === u.id ? ' tlp-user-result--active' : ''}`}
                      onClick={() => pickUser(u)}
                    >
                      <span className="tlp-user-result-name">{u.full_name}</span>
                      <span className="tlp-user-role-badge">{u.roles?.name}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
            <div className="tlp-user-modal-footer">
              <button type="button" className="rf-btn rf-btn--cancel" onClick={() => setShowUserModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Revoke confirmation modal ── */}
      {pendingRevoke && (
        <div className="rf-modal-backdrop" onClick={() => !revoking && setPendingRevoke(null)}>
          <div className="rf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rf-modal-icon">
              <IconWarning />
            </div>
            <p className="rf-modal-text">
              ¿Revocar el permiso de <strong>{pendingRevoke.user?.full_name}</strong> para el{' '}
              {formatDayLabel(pendingRevoke.log_date)}? El usuario dejará de poder editar ese día de inmediato.
            </p>
            <div className="rf-modal-actions">
              <button
                className="rf-btn rf-btn--modal-back"
                onClick={() => setPendingRevoke(null)}
                type="button"
                disabled={revoking}
              >
                No, mantener
              </button>
              <button
                className="rf-btn rf-btn--modal-confirm"
                onClick={confirmRevoke}
                type="button"
                disabled={revoking}
              >
                {revoking ? 'Revocando…' : 'Sí, revocar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimeLogPermissions;
